import { DATE_FORMAT, TIME_FORMAT } from '@bo-constants/member/members';
import type { DateRange, DateTimeFormat } from '@library/types/dates';
import type {
  Deposit,
  UserCoordinates,
  Withdrawal
} from '@library/types/payments';
import {
  LOCATION_NOT_FOUND,
  colLgWidth,
  colMdWidth,
  colSmWidth,
  colXsWidth
} from '@bo-constants/member/memberPayments';
import type {
  MemberPaymentsColumns,
  MemberPaymentsSearchFilters
} from '@library/types/memberPayments';
import {
  depositMethodDisplay,
  depositStatusDisplay,
  withdrawalMethodDisplay,
  withdrawalStatusDisplay
} from '@library/component-utils/paymentsfilters';
import { getMethodInfo, getMethodInfoLines } from '@bo-utils/payments';
import { DateTime } from 'luxon';
import type { TableColumn } from '@library/types/utils';
import { convertCentsToDollars } from '@library/utils';

export const getMemberPaymentsDateTime = (date: string): DateTimeFormat => {
  const dateTime = DateTime.fromFormat(date, 'yyyy-LL-dd HH:mm:ss ZZZ');

  return {
    date: dateTime.toFormat(DATE_FORMAT),
    time: dateTime.toFormat(TIME_FORMAT)
  };
};

export const filterMemberDeposits = (
  deposits: Array<Deposit>,
  paymentsSearchFilters: MemberPaymentsSearchFilters
): Array<Deposit> => {
  const { dateRange, depositMethodType, depositStatus } = paymentsSearchFilters;
  const { fromDate, toDate } = dateRange;

  const isSearchEmpty =
    !fromDate && !toDate && !depositMethodType && !depositStatus;

  if (isSearchEmpty) {
    return deposits;
  }

  return deposits.filter((deposit) => {
    let isValid = true;

    const depositDate = DateTime.fromFormat(
      deposit.created,
      'yyyy-LL-dd HH:mm:ss ZZZ'
    );

    if (fromDate) {
      isValid = isValid && depositDate >= fromDate.startOf('day');
    }

    if (toDate) {
      isValid = isValid && depositDate <= toDate.endOf('day');
    }

    if (depositMethodType && depositMethodType !== '1A') {
      isValid = isValid && deposit.method === depositMethodType;
    }

    if (depositStatus && depositStatus !== '1A') {
      isValid = isValid && deposit.status === depositStatus;
    }

    return isValid;
  });
};

export const filterMemberWithdrawals = (
  withdrawals: Array<Withdrawal>,
  paymentsSearchFilters: MemberPaymentsSearchFilters
): Array<Withdrawal> => {
  const { dateRange, withdrawalMethod, withdrawalStatus } =
    paymentsSearchFilters;
  const { fromDate, toDate } = dateRange;

  const isSearchEmpty =
    !fromDate && !toDate && !withdrawalMethod && !withdrawalStatus;

  if (isSearchEmpty) {
    return withdrawals;
  }

  return withdrawals.filter((withdrawal) => {
    let isValid = true;

    const withdrawalDate = DateTime.fromFormat(
      withdrawal.created,
      'yyyy-LL-dd HH:mm:ss ZZZ'
    );

    if (fromDate) {
      isValid = isValid && withdrawalDate >= fromDate.startOf('day');
    }

    if (toDate) {
      isValid = isValid && withdrawalDate <= toDate.endOf('day');
    }

    if (withdrawalMethod && withdrawalMethod !== '1A') {
      isValid = isValid && withdrawal.method === withdrawalMethod;
    }

    if (withdrawalStatus && withdrawalStatus !== '1A') {
      isValid = isValid && withdrawal.status === withdrawalStatus;
    }

    return isValid;
  });
};

export const formatMemberLocationDetails = (location: UserCoordinates) => {
  const { lat, latitude, long, longitude, stateAbbreviation } = location;

  const formattedLatitude = String(
    latitude ?? lat ?? LOCATION_NOT_FOUND.latitude
  );
  const formattedLongitude = String(
    longitude ?? long ?? LOCATION_NOT_FOUND.longitude
  );
  const formattedStateAbbreviation =
    stateAbbreviation ?? LOCATION_NOT_FOUND.stateAbbreviation;

  return {
    latitude: formattedLatitude,
    longitude: formattedLongitude,
    stateAbbreviation: formattedStateAbbreviation
  };
};

export const formatMemberDepositDetails = (deposit: Deposit) => {
  const {
    amount,
    bank,
    card,
    created,
    location,
    method,
    refundDetails,
    paypal,
    status,
    updated,
    venmo
  } = deposit;

  const {
    latitude: formattedLatitude,
    longitude: formattedLongitude,
    stateAbbreviation
  } = formatMemberLocationDetails(location);

  const formattedAmount = convertCentsToDollars(amount);

  const formattedMethodInfo = getMethodInfo(
    method,
    depositMethodDisplay(method),
    card,
    bank
  );

  const {
    methodLine1: formattedMethodDetails,
    methodLine2: formattedServiceDetails
  } = getMethodInfoLines(
    method,
    depositMethodDisplay(method),
    card,
    bank,
    paypal,
    venmo
  );

  const createdDateTime = getMemberPaymentsDateTime(created);
  const updatedDateTime = getMemberPaymentsDateTime(updated);
  const refundedDateTime = refundDetails?.refundedAt
    ? getMemberPaymentsDateTime(refundDetails.refundedAt)
    : undefined;

  const depositStatus = depositStatusDisplay(status);

  return {
    createdDateTime,
    depositStatus,
    formattedAmount,
    formattedLatitude,
    formattedLongitude,
    formattedMethodDetails,
    formattedMethodInfo,
    formattedServiceDetails,
    refundedDateTime,
    stateAbbreviation,
    updatedDateTime
  };
};

export const formatMemberWithDrawalDetails = (withdrawal: Withdrawal) => {
  const {
    amount,
    bank,
    card,
    created,
    location,
    method,
    paypal,
    status,
    updated,
    venmo
  } = withdrawal;

  const {
    latitude: formattedLatitude,
    longitude: formattedLongitude,
    stateAbbreviation
  } = formatMemberLocationDetails(location);

  const formattedAmount = convertCentsToDollars(amount);

  const formattedMethodInfo = getMethodInfo(
    method,
    withdrawalMethodDisplay(method),
    card,
    bank
  );

  const {
    methodLine1: formattedMethodDetails,
    methodLine2: formattedServiceDetails
  } = getMethodInfoLines(
    method,
    withdrawalMethodDisplay(method),
    card,
    bank,
    paypal,
    venmo
  );

  const createdDateTime = getMemberPaymentsDateTime(created);
  const updatedDateTime = getMemberPaymentsDateTime(updated);

  const withdrawalStatus = withdrawalStatusDisplay(status);

  return {
    createdDateTime,
    formattedAmount,
    formattedLatitude,
    formattedLongitude,
    formattedMethodDetails,
    formattedMethodInfo,
    formattedServiceDetails,
    stateAbbreviation,
    updatedDateTime,
    withdrawalStatus
  };
};

export const getMemberPaymentsTableHeaders = (
  paymentType: 'deposit' | 'withdrawal'
): Array<TableColumn<MemberPaymentsColumns>> => {
  const idPrefix = paymentType === 'deposit' ? 'Deposit' : 'Withdrawal';

  return [
    {
      column: 'id',
      essential: true,
      label: `${idPrefix} ID`,
      sortEnabled: false,
      width: colLgWidth
    },
    {
      column: 'amount',
      essential: true,
      label: 'Amount',
      sortEnabled: false,
      width: colSmWidth
    },
    {
      column: 'method',
      essential: true,
      label: 'Method',
      sortEnabled: false,
      width: colMdWidth
    },
    {
      column: 'state',
      essential: true,
      label: 'State',
      sortEnabled: false,
      width: colXsWidth
    },
    {
      column: 'created_at',
      essential: true,
      label: 'Created At',
      sortEnabled: false,
      width: colMdWidth
    },
    {
      column: 'status',
      essential: true,
      label: 'Status',
      sortEnabled: false,
      width: colSmWidth
    }
  ];
};

export const memberPaymentsDateRangeRequest = (
  dateRange: Partial<DateRange>
) => {
  const { fromDate, toDate } = dateRange;
  const dateFormat = 'yyyy-MM-dd HH:mm:ss';

  const formattedFromDate = fromDate?.startOf('day').toFormat(dateFormat);
  const formattedToDate = toDate?.endOf('day').toFormat(dateFormat);

  return { formattedFromDate, formattedToDate };
};
